export type PdfTextItem = {
  str: string
  transform: readonly number[]
  width: number
}

export type QuoteBox = {
  left: number
  top: number
  width: number
  height: number
}

export type QuoteMatch = {
  boxes: readonly QuoteBox[]
  guessed: boolean
}

type FoldedCharacter = { item: number; index: number; opensToken: boolean; closesToken: boolean }

type FoldedText = {
  items: readonly PdfTextItem[]
  text: string
  characters: (FoldedCharacter | undefined)[]
}

export type FoldedPage = {
  storedOrder: FoldedText
  visualOrder: FoldedText
}

type Span = { left: number; right: number; baseline: number; height: number }

type PartMatch = { start: number; part: string }

const LETTER_OR_DIGIT = /[\p{L}\p{N}]/u
const TOKEN_JOINER = /[.,/'’-]/
const LINE_BREAK = /\r?\n|\t| {2,}/
const LINE_OR_LABEL_BREAK = /\r?\n|\t| {2,}|:\s+/
const LABEL_END = /:\s+/
const RUN_BREAK = '|'
const LEVEL_SLOPE = 0.035
const LINE_DRIFT_HEIGHTS = 0.5
const WORD_GAP_HEIGHTS = 0.2
const COLUMN_GAP_HEIGHTS = 2
const VALUE_DROP_HEIGHTS = 3
const VALUE_REACH_HEIGHTS = 10
const VALUE_OVERLAP_SLACK_HEIGHTS = 1
const SHORTEST_LONE_VALUE_LENGTH = 5
const NO_MATCH: QuoteMatch = { boxes: [], guessed: false }

function fold(character: string): string {
  let folded = ''
  for (const one of character.normalize('NFKC')) {
    if (LETTER_OR_DIGIT.test(one)) folded += one.toLowerCase()
  }
  return folded
}

function foldQuote(quote: string): string {
  let folded = ''
  for (const character of quote) folded += fold(character)
  return folded
}

function at(matrix: readonly number[], index: number): number {
  return matrix[index] ?? 0
}

function baseline(item: PdfTextItem): number {
  return at(item.transform, 5)
}

function leftEdge(item: PdfTextItem): number {
  return at(item.transform, 4)
}

function rightEdge(item: PdfTextItem): number {
  return leftEdge(item) + item.width
}

function textHeight(item: PdfTextItem): number {
  return Math.hypot(at(item.transform, 2), at(item.transform, 3))
}

function isLevel(item: PdfTextItem): boolean {
  return Math.abs(at(item.transform, 1)) <= LEVEL_SLOPE * Math.abs(at(item.transform, 0))
}

function onSameLine(one: PdfTextItem, other: PdfTextItem): boolean {
  const drift = LINE_DRIFT_HEIGHTS * Math.min(textHeight(one), textHeight(other))
  return Math.abs(baseline(one) - baseline(other)) <= drift
}

function touches(left: PdfTextItem | undefined, right: PdfTextItem | undefined): boolean {
  if (left === undefined || right === undefined || !onSameLine(left, right)) return false
  const gap = leftEdge(right) - rightEdge(left)
  return Math.abs(gap) <= WORD_GAP_HEIGHTS * Math.min(textHeight(left), textHeight(right))
}

function isWordCharacter(character: string): boolean {
  return fold(character) !== ''
}

function continuesToken(neighbour: string, beyond: string): boolean {
  return isWordCharacter(neighbour) || (TOKEN_JOINER.test(neighbour) && isWordCharacter(beyond))
}

function foldText(runs: readonly (readonly PdfTextItem[])[]): FoldedText {
  const items: PdfTextItem[] = []
  const characters: (FoldedCharacter | undefined)[] = []
  let text = ''
  runs.forEach((run, runIndex) => {
    if (runIndex > 0) {
      text += RUN_BREAK
      characters.push(undefined)
    }
    run.forEach((item, position) => {
      const itemIndex = items.length
      items.push(item)
      const previous = run[position - 1]
      const next = run[position + 1]
      const before = previous !== undefined && touches(previous, item) ? previous.str.slice(-2) : ''
      const after = next !== undefined && touches(item, next) ? next.str.slice(0, 2) : ''
      const context = before + item.str + after
      for (let index = 0; index < item.str.length; index += 1) {
        const offset = before.length + index
        const folded = fold(item.str.charAt(index))
        text += folded
        for (let count = 0; count < folded.length; count += 1) {
          characters.push({
            item: itemIndex,
            index,
            opensToken: count === 0 && !continuesToken(context.charAt(offset - 1), context.charAt(offset - 2)),
            closesToken:
              count === folded.length - 1 && !continuesToken(context.charAt(offset + 1), context.charAt(offset + 2))
          })
        }
      }
    })
  })
  return { items, text, characters }
}

function closesGap(run: readonly PdfTextItem[], item: PdfTextItem): boolean {
  const previous = run.at(-1)
  return previous !== undefined && leftEdge(item) - rightEdge(previous) <= COLUMN_GAP_HEIGHTS * textHeight(item)
}

function splitAtColumnGaps(line: readonly PdfTextItem[]): PdfTextItem[][] {
  const runs: PdfTextItem[][] = []
  for (const item of line) {
    const run = runs.at(-1)
    if (run !== undefined && closesGap(run, item)) run.push(item)
    else runs.push([item])
  }
  return runs
}

function visualRuns(items: readonly PdfTextItem[]): PdfTextItem[][] {
  const printed = items.filter((item) => foldQuote(item.str) !== '')
  const level = printed.filter(isLevel).sort((left, right) => baseline(right) - baseline(left))
  const lines: PdfTextItem[][] = []
  for (const item of level) {
    const line = lines.at(-1)
    const first = line?.[0]
    if (line !== undefined && first !== undefined && onSameLine(first, item)) line.push(item)
    else lines.push([item])
  }
  const runs = lines.flatMap((line) =>
    splitAtColumnGaps([...line].sort((left, right) => leftEdge(left) - leftEdge(right)))
  )
  const rotated = printed.filter((item) => !isLevel(item))
  return rotated.length === 0 ? runs : [...runs, rotated]
}

export function foldPage(items: readonly PdfTextItem[]): FoldedPage {
  return { storedOrder: foldText([items]), visualOrder: foldText(visualRuns(items)) }
}

function combine(viewport: readonly number[], text: readonly number[]): number[] {
  return [
    at(viewport, 0) * at(text, 0) + at(viewport, 2) * at(text, 1),
    at(viewport, 1) * at(text, 0) + at(viewport, 3) * at(text, 1),
    at(viewport, 0) * at(text, 2) + at(viewport, 2) * at(text, 3),
    at(viewport, 1) * at(text, 2) + at(viewport, 3) * at(text, 3),
    at(viewport, 0) * at(text, 4) + at(viewport, 2) * at(text, 5) + at(viewport, 4),
    at(viewport, 1) * at(text, 4) + at(viewport, 3) * at(text, 5) + at(viewport, 5)
  ]
}

function itemBox(item: PdfTextItem, viewport: readonly number[], from: number, to: number): QuoteBox | null {
  const length = item.str.length
  if (length === 0) return null
  const placed = combine(viewport, item.transform)
  const scale = Math.hypot(at(viewport, 0), at(viewport, 1))
  const lineHeight = Math.hypot(at(placed, 2), at(placed, 3))
  const itemWidth = item.width * scale
  const width = (itemWidth * (to - from)) / length
  if (width <= 0 || lineHeight <= 0) return null
  return {
    left: at(placed, 4) + (itemWidth * from) / length,
    top: at(placed, 5) - lineHeight,
    width,
    height: lineHeight
  }
}

function boxesForMatch(reading: FoldedText, viewport: readonly number[], start: number, length: number): QuoteBox[] {
  const spans = new Map<number, { from: number; to: number }>()
  for (let offset = 0; offset < length; offset += 1) {
    const owner = reading.characters[start + offset]
    if (owner === undefined) continue
    const span = spans.get(owner.item)
    spans.set(
      owner.item,
      span === undefined
        ? { from: owner.index, to: owner.index + 1 }
        : { from: Math.min(span.from, owner.index), to: Math.max(span.to, owner.index + 1) }
    )
  }
  return [...spans].flatMap(([index, span]) => {
    const item = reading.items[index]
    if (item === undefined) return []
    const box = itemBox(item, viewport, span.from, span.to)
    return box === null ? [] : [box]
  })
}

function wholeQuoteBoxes(reading: FoldedText, viewportTransform: readonly number[], needle: string): QuoteBox[] {
  const boxes: QuoteBox[] = []
  let start = reading.text.indexOf(needle)
  while (start !== -1) {
    boxes.push(...boxesForMatch(reading, viewportTransform, start, needle.length))
    start = reading.text.indexOf(needle, start + needle.length)
  }
  return boxes
}

function partsInOrder(reading: FoldedText, parts: readonly string[], from: number): PartMatch[] | null {
  const matches: PartMatch[] = []
  let cursor = from
  for (const part of parts) {
    const start = reading.text.indexOf(part, cursor)
    if (start === -1) return null
    matches.push({ start, part })
    cursor = start + part.length
  }
  return matches
}

function brokenQuoteBoxes(
  reading: FoldedText,
  viewportTransform: readonly number[],
  parts: readonly string[]
): QuoteBox[] {
  const boxes: QuoteBox[] = []
  let matches = partsInOrder(reading, parts, 0)
  while (matches !== null) {
    for (const { start, part } of matches) boxes.push(...boxesForMatch(reading, viewportTransform, start, part.length))
    const last = matches.at(-1)
    matches = last === undefined ? null : partsInOrder(reading, parts, last.start + last.part.length)
  }
  return boxes
}

function isWholeToken(reading: FoldedText, start: number, length: number): boolean {
  return reading.characters[start]?.opensToken === true && reading.characters[start + length - 1]?.closesToken === true
}

function indexOfToken(reading: FoldedText, part: string, from: number): number {
  let start = reading.text.indexOf(part, from)
  while (start !== -1 && !isWholeToken(reading, start, part.length)) start = reading.text.indexOf(part, start + 1)
  return start
}

function characterEdge(item: PdfTextItem, index: number): number {
  return item.str.length === 0 ? leftEdge(item) : leftEdge(item) + (item.width * index) / item.str.length
}

function spanOf(reading: FoldedText, start: number, length: number): Span | undefined {
  const first = reading.characters[start]
  const last = reading.characters[start + length - 1]
  const firstItem = first === undefined ? undefined : reading.items[first.item]
  const lastItem = last === undefined ? undefined : reading.items[last.item]
  if (first === undefined || last === undefined || firstItem === undefined || lastItem === undefined) return undefined
  return {
    left: characterEdge(firstItem, first.index),
    right: characterEdge(lastItem, last.index + 1),
    baseline: baseline(firstItem),
    height: Math.max(textHeight(firstItem), textHeight(lastItem))
  }
}

function followsOnLine(label: Span, value: Span): boolean {
  const height = Math.min(label.height, value.height)
  if (Math.abs(label.baseline - value.baseline) > LINE_DRIFT_HEIGHTS * height) return false
  const gap = value.left - label.right
  return gap >= -height && gap <= VALUE_REACH_HEIGHTS * height
}

function followsBelow(label: Span, value: Span): boolean {
  const height = Math.min(label.height, value.height)
  const drop = label.baseline - value.baseline
  if (drop <= LINE_DRIFT_HEIGHTS * height || drop > VALUE_DROP_HEIGHTS * height) return false
  const slack = VALUE_OVERLAP_SLACK_HEIGHTS * height
  return value.left <= label.right + slack && value.right >= label.left - slack
}

function separation(label: Span, value: Span): number {
  return Math.hypot(Math.max(0, value.left - label.right), label.baseline - value.baseline)
}

function indexOfFollowingToken(reading: FoldedText, part: string, previous: PartMatch): number {
  const label = spanOf(reading, previous.start, previous.part.length)
  if (label === undefined) return -1
  let nearest = -1
  let nearestSeparation = Infinity
  let start = indexOfToken(reading, part, previous.start + previous.part.length)
  while (start !== -1) {
    const value = spanOf(reading, start, part.length)
    if (value !== undefined && (followsOnLine(label, value) || followsBelow(label, value))) {
      const apart = separation(label, value)
      if (apart < nearestSeparation) {
        nearest = start
        nearestSeparation = apart
      }
    }
    start = indexOfToken(reading, part, start + 1)
  }
  return nearest
}

function followingParts(reading: FoldedText, first: PartMatch, rest: readonly string[]): PartMatch[] | null {
  const matches = [first]
  let previous = first
  for (const part of rest) {
    const start = indexOfFollowingToken(reading, part, previous)
    if (start === -1) return null
    previous = { start, part }
    matches.push(previous)
  }
  return matches
}

function labelThenValue(reading: FoldedText, parts: readonly string[], from: number): PartMatch[] | null {
  const [first, ...rest] = parts
  if (first === undefined) return null
  let start = indexOfToken(reading, first, from)
  while (start !== -1) {
    const matches = followingParts(reading, { start, part: first }, rest)
    if (matches !== null) return matches
    start = indexOfToken(reading, first, start + 1)
  }
  return null
}

function labelledQuoteBoxes(
  reading: FoldedText,
  viewportTransform: readonly number[],
  parts: readonly string[]
): QuoteBox[] {
  const boxes: QuoteBox[] = []
  let matches = labelThenValue(reading, parts, 0)
  while (matches !== null) {
    for (const { start, part } of matches) boxes.push(...boxesForMatch(reading, viewportTransform, start, part.length))
    const last = matches.at(-1)
    matches = last === undefined ? null : labelThenValue(reading, parts, last.start + last.part.length)
  }
  return boxes
}

function tokenBoxes(reading: FoldedText, viewportTransform: readonly number[], needle: string): QuoteBox[] {
  const boxes: QuoteBox[] = []
  let start = indexOfToken(reading, needle, 0)
  while (start !== -1) {
    boxes.push(...boxesForMatch(reading, viewportTransform, start, needle.length))
    start = indexOfToken(reading, needle, start + needle.length)
  }
  return boxes
}

function storedThenVisual(page: FoldedPage, search: (reading: FoldedText) => QuoteBox[]): QuoteBox[] {
  const stored = search(page.storedOrder)
  return stored.length > 0 ? stored : search(page.visualOrder)
}

function foldedParts(quote: string, pattern: RegExp): string[] {
  return quote
    .split(pattern)
    .map(foldQuote)
    .filter((part) => part !== '')
}

function valueAfterLabel(quote: string): string {
  return foldQuote(quote.split(LABEL_END).at(-1) ?? '')
}

export function matchQuote(page: FoldedPage, viewportTransform: readonly number[], quote: string): QuoteMatch {
  const needle = foldQuote(quote)
  if (needle === '') return NO_MATCH
  const whole = storedThenVisual(page, (reading) => wholeQuoteBoxes(reading, viewportTransform, needle))
  if (whole.length > 0) return { boxes: whole, guessed: false }
  const hasLabel = LABEL_END.test(quote)
  const lines = foldedParts(quote, LINE_BREAK)
  if (!hasLabel && lines.length > 1) {
    const broken = storedThenVisual(page, (reading) => brokenQuoteBoxes(reading, viewportTransform, lines))
    if (broken.length > 0) return { boxes: broken, guessed: false }
  }
  const labelAndValue = foldedParts(quote, LINE_OR_LABEL_BREAK)
  if (hasLabel && labelAndValue.length > 1) {
    const paired = storedThenVisual(page, (reading) => labelledQuoteBoxes(reading, viewportTransform, labelAndValue))
    if (paired.length > 0) return { boxes: paired, guessed: false }
  }
  const value = valueAfterLabel(quote)
  if (!hasLabel || value.length < SHORTEST_LONE_VALUE_LENGTH) return NO_MATCH
  return { boxes: storedThenVisual(page, (reading) => tokenBoxes(reading, viewportTransform, value)), guessed: true }
}
